// DEMS AI Agents Architecture Page
// Visual representation of AI agent ecosystem for evidence management
import { useState, useEffect } from 'react';
import { 
  Brain, 
  MessageSquare, 
  Shield, 
  Bell, 
  Search, 
  FileText,
  AlertTriangle,
  CheckCircle,
  Clock,
  Zap,
  Eye,
  Users,
  Lock,
  Database,
  ArrowRight,
  Sparkles
} from 'lucide-react';

interface AgentStatus {
  id: string;
  name: string;
  type: 'conversational' | 'background';
  status: 'active' | 'idle' | 'processing';
  lastActivity?: string;
  processedToday?: number;
  description: string;
  persona: string[];
  icon: React.ReactNode;
}

interface AgentActivity {
  id: string;
  agentId: string;
  agentName: string;
  action: string;
  timestamp: Date;
  details?: string;
  caseId?: string;
}

export function AIAgents() {
  const [agents, setAgents] = useState<AgentStatus[]>([
    {
      id: 'discovery-copilot',
      name: 'Evidence Discovery Copilot',
      type: 'conversational',
      status: 'active',
      lastActivity: '2 minutes ago',
      processedToday: 47,
      description: 'Natural language search across case evidence. Ask questions like "Find all videos from February near Main Street."',
      persona: ['Attorney', 'Law Enforcement'],
      icon: <Search className="w-6 h-6" />
    },
    {
      id: 'custody-explainer',
      name: 'Chain of Custody Explainer',
      type: 'conversational',
      status: 'idle',
      lastActivity: '15 minutes ago',
      processedToday: 12,
      description: 'Explains complex custody chains in plain English. Summarizes 50+ events into digestible narratives.',
      persona: ['Judge', 'Attorney', 'Case Manager'],
      icon: <FileText className="w-6 h-6" />
    },
    {
      id: 'triage-agent',
      name: 'Public Submission Triage',
      type: 'background',
      status: 'processing',
      lastActivity: 'Now',
      processedToday: 156,
      description: 'Auto-classifies incoming public submissions. Routes relevant evidence, flags spam, suggests case linkage.',
      persona: ['Case Manager', 'Clerk'],
      icon: <Users className="w-6 h-6" />
    },
    {
      id: 'anomaly-detector',
      name: 'Security Anomaly Detector',
      type: 'background',
      status: 'active',
      lastActivity: '30 seconds ago',
      processedToday: 2847,
      description: 'Monitors access patterns for unusual behavior. Alerts on bulk downloads, after-hours access, or role violations.',
      persona: ['System Admin', 'Case Manager'],
      icon: <Shield className="w-6 h-6" />
    },
    {
      id: 'integrity-monitor',
      name: 'Evidence Integrity Monitor',
      type: 'background',
      status: 'active',
      lastActivity: '1 minute ago',
      processedToday: 523,
      description: 'Continuously verifies evidence hash integrity. Detects tampering attempts and custody chain gaps.',
      persona: ['System Admin', 'All Users'],
      icon: <Lock className="w-6 h-6" />
    }
  ]);

  const [activities] = useState<AgentActivity[]>([
    {
      id: '1',
      agentId: 'triage-agent',
      agentName: 'Triage Agent',
      action: 'Classified submission as "Relevant"',
      timestamp: new Date(Date.now() - 30000),
      details: 'Document matches open case keywords',
      caseId: '2026-CR-0042'
    },
    {
      id: '2',
      agentId: 'anomaly-detector',
      agentName: 'Anomaly Detector',
      action: 'Access pattern verified normal',
      timestamp: new Date(Date.now() - 60000),
      details: 'User access within expected parameters'
    },
    {
      id: '3',
      agentId: 'integrity-monitor',
      agentName: 'Integrity Monitor',
      action: 'Hash verification complete',
      timestamp: new Date(Date.now() - 120000),
      details: '15 evidence files verified, all intact'
    },
    {
      id: '4',
      agentId: 'discovery-copilot',
      agentName: 'Discovery Copilot',
      action: 'Answered query for Sarah Mitchell',
      timestamp: new Date(Date.now() - 180000),
      details: '"Find surveillance videos from Feb 15" - 3 results',
      caseId: '2026-CR-0042'
    },
    {
      id: '5',
      agentId: 'triage-agent',
      agentName: 'Triage Agent',
      action: 'Flagged submission for review',
      timestamp: new Date(Date.now() - 240000),
      details: 'Low confidence classification (68%)',
      caseId: '2026-CV-0108'
    }
  ]);

  // Simulate background agent activity
  useEffect(() => {
    const interval = setInterval(() => {
      setAgents(prev => prev.map(agent => {
        if (agent.type === 'background') {
          return {
            ...agent,
            processedToday: (agent.processedToday || 0) + Math.floor(Math.random() * 3),
            lastActivity: agent.id === 'triage-agent' ? 'Now' : 
                         agent.id === 'anomaly-detector' ? `${Math.floor(Math.random() * 30)} seconds ago` :
                         `${Math.floor(Math.random() * 2) + 1} minute ago`
          };
        }
        return agent;
      }));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: AgentStatus['status']) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'processing': return 'bg-blue-500 animate-pulse';
      case 'idle': return 'bg-gray-400';
    }
  };

  const getStatusText = (status: AgentStatus['status']) => {
    switch (status) {
      case 'active': return 'Active';
      case 'processing': return 'Processing';
      case 'idle': return 'Idle';
    }
  };

  const formatTime = (date: Date) => {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    return `${Math.floor(minutes / 60)}h ago`;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <Brain className="w-8 h-8 text-purple-600" />
            AI Agent Ecosystem
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Intelligent agents working to secure evidence and assist court personnel
          </p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
          <Sparkles className="w-5 h-5 text-purple-600" />
          <span className="text-purple-700 dark:text-purple-300 font-medium">
            {agents.filter(a => a.status !== 'idle').length} agents active
          </span>
        </div>
      </div>

      {/* Architecture Diagram */}
      <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 rounded-xl p-8 border border-slate-200 dark:border-slate-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
          <Database className="w-5 h-5" />
          Agent Architecture
        </h2>
        
        <div className="relative">
          {/* Three column layout */}
          <div className="grid grid-cols-3 gap-8">
            {/* Left: User-Facing Agents */}
            <div className="space-y-4">
              <div className="text-center mb-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                  <MessageSquare className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Conversational Agents</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">Users interact directly</p>
              </div>
              
              {agents.filter(a => a.type === 'conversational').map(agent => (
                <div 
                  key={agent.id}
                  className="bg-white dark:bg-slate-700 rounded-lg p-4 shadow-sm border-l-4 border-blue-500 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-blue-600">
                      {agent.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900 dark:text-white text-sm">{agent.name}</h3>
                        <span className={`w-2 h-2 rounded-full ${getStatusColor(agent.status)}`} />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{agent.description}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {agent.persona.map(p => (
                          <span key={p} className="px-2 py-0.5 bg-gray-100 dark:bg-slate-600 rounded text-xs text-gray-600 dark:text-gray-300">
                            {p}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Center: Core System */}
            <div className="flex flex-col items-center justify-center">
              <div className="relative">
                {/* Central hub */}
                <div className="w-40 h-40 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
                  <div className="text-center text-white">
                    <Brain className="w-12 h-12 mx-auto mb-2" />
                    <span className="text-sm font-medium">DEMS AI Core</span>
                  </div>
                </div>
                
                {/* Orbiting elements */}
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-white dark:bg-slate-700 rounded-full shadow-md flex items-center justify-center">
                  <Search className="w-6 h-6 text-purple-600" />
                </div>
                <div className="absolute -top-4 -right-4 w-12 h-12 bg-white dark:bg-slate-700 rounded-full shadow-md flex items-center justify-center">
                  <Shield className="w-6 h-6 text-green-600" />
                </div>
                <div className="absolute -bottom-4 -left-4 w-12 h-12 bg-white dark:bg-slate-700 rounded-full shadow-md flex items-center justify-center">
                  <Bell className="w-6 h-6 text-orange-600" />
                </div>
                <div className="absolute -bottom-4 -right-4 w-12 h-12 bg-white dark:bg-slate-700 rounded-full shadow-md flex items-center justify-center">
                  <Eye className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              
              {/* Arrows */}
              <div className="absolute left-1/4 top-1/2 transform -translate-y-1/2">
                <ArrowRight className="w-8 h-8 text-gray-300" />
              </div>
              <div className="absolute right-1/4 top-1/2 transform -translate-y-1/2 rotate-180">
                <ArrowRight className="w-8 h-8 text-gray-300" />
              </div>
              
              {/* Security badges below */}
              <div className="mt-8 flex flex-wrap justify-center gap-2">
                <div className="flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 rounded text-xs text-green-700 dark:text-green-300">
                  <Lock className="w-3 h-3" />
                  Case-Scoped
                </div>
                <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 rounded text-xs text-blue-700 dark:text-blue-300">
                  <CheckCircle className="w-3 h-3" />
                  Audited
                </div>
                <div className="flex items-center gap-1 px-2 py-1 bg-purple-100 dark:bg-purple-900/30 rounded text-xs text-purple-700 dark:text-purple-300">
                  <FileText className="w-3 h-3" />
                  Cited
                </div>
              </div>
            </div>

            {/* Right: Background Agents */}
            <div className="space-y-4">
              <div className="text-center mb-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 dark:bg-green-900/30 rounded-full">
                  <Zap className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-700 dark:text-green-300">Background Agents</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">Always watching, always protecting</p>
              </div>
              
              {agents.filter(a => a.type === 'background').map(agent => (
                <div 
                  key={agent.id}
                  className="bg-white dark:bg-slate-700 rounded-lg p-4 shadow-sm border-l-4 border-green-500 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-green-50 dark:bg-green-900/30 rounded-lg text-green-600">
                      {agent.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900 dark:text-white text-sm">{agent.name}</h3>
                        <span className={`w-2 h-2 rounded-full ${getStatusColor(agent.status)}`} />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{agent.description}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-400">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {agent.lastActivity}
                        </span>
                        <span className="text-xs font-medium text-green-600">
                          {agent.processedToday?.toLocaleString()} today
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Two column: Agent Cards + Activity Feed */}
      <div className="grid grid-cols-2 gap-6">
        {/* Agent Details */}
        <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Brain className="w-5 h-5 text-purple-600" />
            Agent Status Overview
          </h2>
          
          <div className="space-y-4">
            {agents.map(agent => (
              <div 
                key={agent.id}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${agent.type === 'conversational' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600' : 'bg-green-100 dark:bg-green-900/30 text-green-600'}`}>
                    {agent.icon}
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white text-sm">{agent.name}</h3>
                    <p className="text-xs text-gray-500">{agent.type === 'conversational' ? 'Conversational' : 'Background'}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${getStatusColor(agent.status)}`} />
                    <span className="text-sm text-gray-600 dark:text-gray-300">{getStatusText(agent.status)}</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{agent.processedToday?.toLocaleString()} processed</p>
                </div>
              </div>
            ))}
          </div>

          {/* Trust indicators */}
          <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg">
            <h3 className="text-sm font-medium text-purple-900 dark:text-purple-100 mb-3">AI Trust & Safety</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 text-xs text-purple-700 dark:text-purple-300">
                <Shield className="w-4 h-4" />
                <span>Read-only access</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-purple-700 dark:text-purple-300">
                <Lock className="w-4 h-4" />
                <span>Case-scoped queries</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-purple-700 dark:text-purple-300">
                <FileText className="w-4 h-4" />
                <span>Mandatory citations</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-purple-700 dark:text-purple-300">
                <Eye className="w-4 h-4" />
                <span>Full audit logging</span>
              </div>
            </div>
          </div>
        </div>

        {/* Live Activity Feed */}
        <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-orange-500" />
            Live Agent Activity
            <span className="ml-auto flex items-center gap-1 text-xs text-green-600 font-normal">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              Live
            </span>
          </h2>
          
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {activities.map(activity => (
              <div 
                key={activity.id}
                className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                  <Brain className="w-4 h-4 text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-gray-900 dark:text-white">{activity.agentName}</span>
                    <span className="text-xs text-gray-400">{formatTime(activity.timestamp)}</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">{activity.action}</p>
                  {activity.details && (
                    <p className="text-xs text-gray-500 mt-1">{activity.details}</p>
                  )}
                  {activity.caseId && (
                    <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 rounded text-xs text-blue-700 dark:text-blue-300">
                      Case: {activity.caseId}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {/* Activity summary */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-600">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Total activities today</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {agents.reduce((sum, a) => sum + (a.processedToday || 0), 0).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom: How AI Helps Each Role */}
      <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-slate-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">How AI Agents Help Court Personnel</h2>
        
        <div className="grid grid-cols-4 gap-6">
          <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="w-12 h-12 mx-auto mb-3 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="font-medium text-gray-900 dark:text-white mb-2">Attorneys</h3>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              "Find all documents mentioning wire transfers over $10K"
              <br /><br />
              <span className="text-blue-600">→ Discovery Copilot</span>
            </p>
          </div>
          
          <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <div className="w-12 h-12 mx-auto mb-3 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
              <FileText className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="font-medium text-gray-900 dark:text-white mb-2">Case Managers</h3>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Auto-triage 156 submissions today. 12 relevant, 3 need review.
              <br /><br />
              <span className="text-purple-600">→ Triage Agent</span>
            </p>
          </div>
          
          <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <div className="w-12 h-12 mx-auto mb-3 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <Shield className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="font-medium text-gray-900 dark:text-white mb-2">Judges</h3>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              "Summarize custody chain for Exhibit A - 47 events simplified"
              <br /><br />
              <span className="text-green-600">→ Custody Explainer</span>
            </p>
          </div>
          
          <div className="text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
            <div className="w-12 h-12 mx-auto mb-3 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
            </div>
            <h3 className="font-medium text-gray-900 dark:text-white mb-2">Admins</h3>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Alert: Unusual bulk download detected. 47 files in 2 minutes.
              <br /><br />
              <span className="text-orange-600">→ Anomaly Detector</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AIAgents;
